import React from 'react';
import './LobbyControlBar.css';
import PageButton from '../../elements/PageButton/PageButton';
import LobbyBonusToggle from '../LobbyBonusToggle';

/**
 * Отсчет фазы в полосе — рядом с кнопкой, которой фазу закрывают
 * («Подтвердить баны», «Подтвердить выбор», «Начать матч»): в углу шапки его
 * не замечали. Крупные
 * цифры в золотой рамке; последние секунды — красным с пульсом, с 4 с —
 * мигают (см. lobbyCountdownUrgency; звук таймера играет LobbyPage). Значок
 * правил фазы — подсказка по наведению, модалка по нажатию. ИИ-лобби до
 * банов — его «Сбор игроков»/«Старт через», без срочности.
 */
const LobbyBarTimer = ({countdown}) => {
  if (countdown.seconds === null) {
    if (!countdown.aiState) return null;
    return (
      <div className="lobby-bar-timer">
        <span className="lobby-bar-timer_label">{countdown.aiState.prefix}</span>
        <span className="lobby-bar-timer_value">{countdown.aiState.timeText}</span>
      </div>
    );
  }
  return (
    <div className={`lobby-bar-timer lobby-bar-timer_${countdown.urgency}`} role="timer">
      <span className="lobby-bar-timer_label">
        {countdown.isBanPhase ? 'Бан фракций' : 'Матч начнется через'}
        <img
          className="lobby-countdown-info-icon lobby-bar-timer_info"
          src="/img/icons/info.png"
          alt="Правила фазы"
          title={countdown.hintTitle}
          onClick={countdown.onOpenHint}
        />
      </span>
      <span className="lobby-bar-timer_value">
        <span className="lobby-bar-timer_digits">{countdown.seconds}</span> сек
      </span>
    </div>
  );
};

/**
 * Нижняя полоса десктопного лобби во всю ширину экрана: все кнопки управления
 * в одной строке, чтобы не отнимать высоту у карточек. Слева — выход и
 * приглашение, в центре — настройки создателя обычного лобби и бонус лобби,
 * справа — админские кнопки и главная: «Подтвердить баны» / «Изменить баны»
 * в фазе банов, «Подтвердить выбор» / «Изменить выбор» в окне выбора
 * фракции рейтингового лобби (у создателя ручного лобби рядом остается
 * «Начать матч»), иначе «Начать матч» у создателя, — и отсчет фазы рядом с
 * ней (LobbyBarTimer). Условия видимости и
 * обработчики — из LobbyPage (те же, что у мобильной раскладки), pageId и
 * buttonId для аналитики — тоже те же.
 *
 * @param {Object} props
 * @param {Object} props.lobby
 * @param {boolean} props.isCreator
 * @param {Object} props.controls - кнопки и настройки из LobbyPage
 * @param {Object} props.banView - состояние фазы банов из LobbyPage
 * @param {Object} props.selectView - подтверждение выбора фракции из LobbyPage
 * @param {Object} props.countdown - отсчет фазы из LobbyPage
 */
const LobbyControlBar = ({lobby, isCreator, controls, banView, selectView, countdown}) => {
  const isConfirmVisible = banView.isVoter;
  const isFactionConfirmVisible = selectView.isConfirmer;
  const isStartVisible = !isConfirmVisible && controls.isStartVisible;
  const isRightEmpty = !controls.isFieldSelectVisible && !controls.addBotButton && !isConfirmVisible
    && !isFactionConfirmVisible && !isStartVisible;
  const isTimerVisible = countdown.seconds !== null || Boolean(countdown.aiState);
  // Справа пусто — объясняем, кто запустит матч: в обычном лобби (и в ручном
  // рейтинговом админа до его старта) — создатель, иначе он начнется сам. С
  // отсчетом объяснять нечего: «Матч начнется через» говорит то же
  let rightNote = null;
  if (isRightEmpty && !isTimerVisible) {
    const isWaitingForCreator = !lobby.isRanked
      || (Boolean(lobby.creatorId) && !lobby.isAiMatch && !banView.isPhase && !lobby.rankMatchStartAt);
    rightNote = isWaitingForCreator ? 'Матч начнет создатель лобби' : 'Матч начнется автоматически';
  }

  return (
    <footer className="lobby-control-bar">
      <div className="lobby-control-bar_group lobby-control-bar_left">
        {controls.isLeaveVisible && (
          <PageButton className="lobby-bar-button" onClick={controls.onLeave} pageId="LobbyPage" buttonId="leave_lobby">
            Покинуть лобби
          </PageButton>
        )}
        {controls.isInviteVisible && (
          <PageButton className="lobby-bar-button" onClick={controls.onInvite} pageId="LobbyPage" buttonId="invite_friend">
            Пригласить друга
          </PageButton>
        )}
      </div>

      <div className="lobby-control-bar_group lobby-control-bar_center">
        {controls.isCreatorSettingsVisible && (
          <div className="lobby-control-bar_settings">
            {/* Короче, чем на телефоне: в полосе на одну строку каждый
                сантиметр — это перенос кнопок на вторую */}
            <label className="lobby-checkbox lobby-bar-checkbox" title="Автоматический таймер ходов">
              <input
                type="checkbox"
                checked={controls.timerEnabled}
                onChange={controls.onTimerChange}
              />
              <span>Таймер ходов</span>
            </label>
            <label className="lobby-checkbox lobby-bar-checkbox">
              <input
                type="checkbox"
                checked={controls.fastMode}
                onChange={controls.onFastModeChange}
              />
              <span>Быстрый режим</span>
            </label>
          </div>
        )}
        {/* Бонус виден всем участникам обычного лобби, переключает — создатель */}
        {controls.isBonusVisible && (
          <LobbyBonusToggle
            bonusType={lobby.bonusType}
            isCreator={isCreator}
            onSelect={controls.onSelectLobbyBonus}
          />
        )}
      </div>

      <div className="lobby-control-bar_group lobby-control-bar_right">
        {controls.isFieldSelectVisible && (
          <PageButton
            className="lobby-bar-button"
            onClick={controls.onOpenFieldSelect}
            pageId="LobbyPage"
            buttonId="select_field"
          >
            Выбрать поле
          </PageButton>
        )}
        {controls.addBotButton && (
          <PageButton
            className="lobby-bar-button"
            onClick={controls.addBotButton.onClick}
            pageId="LobbyPage"
            buttonId={controls.addBotButton.buttonId}
          >
            Добавить бота
          </PageButton>
        )}
        {/* Подтвердить можно и не всех забанив — подсказка, кто остался. Левее
            отсчета: тот стоит вплотную к кнопке, которой фазу закрывают */}
        {isConfirmVisible && !banView.isOwnConfirmed && banView.missingTargetNames.length > 0 && (
          <span
            className="lobby-control-bar_missing"
            title={`Вы еще не выбрали бан: ${banView.missingTargetNames.join(', ')}`}
          >
            Без бана: {banView.missingTargetNames.join(', ')}
          </span>
        )}
        {/* Подтверждать нечего, пока фракция не выбрана: объясняем, почему
            кнопка неактивна */}
        {isFactionConfirmVisible && !selectView.isOwnConfirmed && !selectView.hasOwnFaction && (
          <span className="lobby-control-bar_missing">Сначала выберите фракцию</span>
        )}
        <LobbyBarTimer countdown={countdown}/>
        {isConfirmVisible && (
          <div className="lobby-control-bar_confirm">
            <PageButton
              className={`lobby-bar-button lobby-bar-button_with-hint ${
                banView.isOwnConfirmed ? '' : 'lobby-bar-button_primary'} ${
                !banView.isOwnConfirmed && !banView.isConfirmDisabled ? 'lobby-button-pulse' : ''}`}
              onClick={() => controls.onConfirmBans(!banView.isOwnConfirmed)}
              disabled={banView.isConfirmDisabled}
              pageId="LobbyPage"
              buttonId={banView.isOwnConfirmed ? 'edit_faction_bans' : 'confirm_faction_bans'}
            >
              <span className="lobby-bar-button_label">
                {banView.isOwnConfirmed ? 'Изменить баны' : 'Подтвердить баны'}
              </span>
              <span className="lobby-bar-button_hint">
                Подтвердили {banView.confirmedCount} из {banView.voterCount}
              </span>
            </PageButton>
          </div>
        )}
        {/* Окно выбора фракции: выбор фиксирует подтверждение, готовы все —
            матч стартует досрочно. Под подписью — сколько игроков готово */}
        {isFactionConfirmVisible && (
          <div className="lobby-control-bar_confirm">
            <PageButton
              className={`lobby-bar-button lobby-bar-button_with-hint ${
                selectView.isOwnConfirmed ? '' : 'lobby-bar-button_primary'} ${
                !selectView.isOwnConfirmed && !selectView.isConfirmDisabled ? 'lobby-button-pulse' : ''}`}
              onClick={() => controls.onConfirmFactionSelection(!selectView.isOwnConfirmed)}
              disabled={selectView.isConfirmDisabled}
              pageId="LobbyPage"
              buttonId={selectView.isOwnConfirmed ? 'edit_faction_selection' : 'confirm_faction_selection'}
            >
              <span className="lobby-bar-button_label">
                {selectView.isOwnConfirmed ? 'Изменить выбор' : 'Подтвердить выбор'}
              </span>
              <span className="lobby-bar-button_hint">
                Готовы {selectView.readyCount} из {selectView.playerCount}
              </span>
            </PageButton>
          </div>
        )}
        {isStartVisible && (
          <PageButton
            className={`lobby-bar-button lobby-bar-button_primary ${!controls.isStartDisabled ? 'lobby-button-pulse' : ''}`}
            onClick={controls.onStartGame}
            disabled={controls.isStartDisabled}
            pageId="LobbyPage"
            buttonId="start_game"
            buttonValue={`players_count_${lobby.players.length}`}
          >
            Начать матч
          </PageButton>
        )}
        {rightNote && <span className="lobby-control-bar_note">{rightNote}</span>}
      </div>
    </footer>
  );
};

export default LobbyControlBar;
